import { useEffect, useState } from "react";
import { Play, X } from "@phosphor-icons/react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { startLiveTour, useLaunchLiveTour, TOUR_ROLES } from "./LiveTour";
import { KbWalkthrough } from "./KbWalkthrough";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export { useLaunchLiveTour, TOUR_ROLES, startLiveTour };

function useSeen(key) {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(false);
  const seen = hidden || !user || (user.tours_seen || []).includes(key);
  const markSeen = () => { setHidden(true); API.post(`/kb/tours/${key}/seen`).catch(() => {}); };
  return { seen, markSeen, hide: () => setHidden(true) };
}

/* Dashboard: offered once to a new tenant admin — "Take the tour" runs the live spotlight tour. */
export function DashboardTourOffer() {
  const { user } = useAuth();
  const { seen, markSeen, hide } = useSeen("dashboard");
  const launch = useLaunchLiveTour();
  if (seen || !TOUR_ROLES.includes(user?.role) || user?.impersonating) return null;
  return (
    <div className="mb-6 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 animate-fade-in" data-testid="dashboard-tour-offer">
      <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0"><Play size={16} weight="fill" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold font-[Chivo]">New here? Take the two-minute tour</p>
        <p className="text-xs text-muted-foreground">We'll highlight the sidebar, Create button, search, live tiles and settings on your own screen — with narration.</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" onClick={() => { hide(); launch("dashboard"); }} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90" data-testid="dashboard-tour-start">
          <Play size={12} weight="fill" /> Take the tour
        </button>
        <button type="button" onClick={markSeen} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-border text-xs text-muted-foreground hover:bg-muted" data-testid="dashboard-tour-dismiss">
          <X size={12} /> Not now
        </button>
      </div>
    </div>
  );
}

/* Portals: offered once on first sign-in — opens the welcome guide's 60-second narrated walkthrough. */
export function PortalTourOffer({ slug, title = "Watch a 60-second tour", blurb = "A quick narrated look at what you can do here.", openExternal = false, onExternalClose }) {
  const { seen, markSeen, hide } = useSeen(slug);
  const [open, setOpen] = useState(false);
  const [article, setArticle] = useState(null);
  useEffect(() => { if (openExternal) setOpen(true); }, [openExternal]);
  useEffect(() => {
    if (!open || article) return;
    API.get(`/kb/articles/${slug}`).then(({ data }) => setArticle(data)).catch(() => setOpen(false));
  }, [open, slug, article]);
  if (seen && !open) return null;
  const finish = () => { setOpen(false); markSeen(); onExternalClose?.(); };
  return (
    <>
      {!seen && (
        <div className="mb-4 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 flex items-center gap-3 animate-fade-in" data-testid={`portal-tour-offer-${slug}`}>
          <button type="button" onClick={() => { hide(); setOpen(true); }} className="h-9 w-9 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0 hover:scale-105 transition-transform" aria-label={title} data-testid={`portal-tour-watch-${slug}`}>
            <Play size={16} weight="fill" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold font-[Chivo]">{title}</p>
            <p className="text-xs text-muted-foreground truncate">{blurb}</p>
          </div>
          <button type="button" onClick={markSeen} className="p-1.5 rounded hover:bg-muted text-muted-foreground" aria-label="Dismiss" data-testid={`portal-tour-dismiss-${slug}`}><X size={14} /></button>
        </div>
      )}
      <Dialog open={open} onOpenChange={(v) => { if (!v) finish(); }}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid={`portal-tour-dialog-${slug}`}>
          <DialogHeader className="px-5 pt-4 pb-0">
            <DialogTitle className="font-[Chivo]">{article?.title || title}</DialogTitle>
            <DialogDescription>{article?.summary || blurb}</DialogDescription>
          </DialogHeader>
          <div className="px-5 pb-5 pt-3">
            {article ? <KbWalkthrough walkthrough={article.walkthrough} slug={slug} autoPlay onFinished={finish} /> : <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
